import React, { useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { Box, Typography } from '@mui/material';
import {
  Check as CheckIcon,
  PriorityHigh as PriorityIcon,
} from '@mui/icons-material';

/**
 * Swipeable card wrapper for mobile cards
 * @param {React.ReactNode} children - The card content to wrap
 * @param {Function} onComplete - Called when swiped left past threshold
 * @param {Function} onPriority - Called when swiped right past threshold
 * @param {boolean} disabled - Disable swipe actions
 */
export default function SwipeableCard({ children, onComplete, onPriority, disabled = false }) {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const THRESHOLD = 100;

  const handlers = useSwipeable({
    onSwiping: (e) => {
      if (disabled) return;
      // Limit swipe distance
      const offset = Math.max(-150, Math.min(150, e.deltaX));
      setSwipeOffset(offset);
    },
    onSwipedLeft: () => {
      if (disabled) return;
      if (swipeOffset < -THRESHOLD && onComplete) {
        onComplete();
      }
      setSwipeOffset(0);
    },
    onSwipedRight: () => {
      if (disabled) return;
      if (swipeOffset > THRESHOLD && onPriority) {
        onPriority();
      }
      setSwipeOffset(0);
    },
    onTouchEndOrOnMouseUp: () => {
      // Reset if not past threshold
      if (Math.abs(swipeOffset) < THRESHOLD) {
        setSwipeOffset(0);
      }
    },
    trackMouse: false,
    preventScrollOnSwipe: true,
    delta: 10,
  });

  const leftOpacity = Math.min(1, swipeOffset / THRESHOLD);
  const rightOpacity = Math.min(1, -swipeOffset / THRESHOLD);

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', mb: 2 }}>
      {/* Background actions */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          borderRadius: 2,
        }}
      >
        {/* Left action (swipe right) - Priority */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: '#ff9800',
            opacity: leftOpacity,
            transition: 'opacity 0.1s ease',
          }}
        >
          <PriorityIcon sx={{ mr: 1, fontSize: 28 }} />
          <Typography variant="body2" fontWeight={600}>
            Priority
          </Typography>
        </Box>

        {/* Right action (swipe left) - Complete */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: '#4caf50',
            opacity: rightOpacity,
            transition: 'opacity 0.1s ease',
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            Complete
          </Typography>
          <CheckIcon sx={{ ml: 1, fontSize: 28 }} />
        </Box>
      </Box>

      {/* Foreground card */}
      <Box
        {...handlers}
        sx={{
          transform: `translateX(${swipeOffset}px)`,
          transition: swipeOffset === 0 ? 'transform 0.2s ease-out' : 'none',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
